<template>
    <themed-modal
        name="document-edit"
        title="Edit Document"
        size="md"
        @opened="handleOpened"
        @closed="handleClosed"
    >
        <themed-form
            v-if="document"
            :fields="formFields"
            :model-value="document"
            :errors="formErrors"
            layout="grid"
            :columns="2"
            @input="handleInput"
        />

        <template #footer>
            <div class="modal-actions">
                <themed-button variant="secondary" @click="hideModal">
                    Cancel
                </themed-button>
                <themed-button 
                    variant="default" 
                    @click="save"
                    :disabled="saving"
                >
                    {{ saving ? 'Saving...' : 'Save Document' }}
                </themed-button>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
    name: 'EditDocumentV1',

    components: {
        ThemedModal,
        ThemedForm,
        ThemedButton
    },

    props: {
        documentData: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            document: null,
            formErrors: {},
            saving: false
        };
    },

    computed: {
        ...mapState(['documents']),

        formFields() {
            const fields = [
                {
                    name: 'name',
                    type: 'input',
                    label: 'Name',
                    required: true,
                    col: 2 // Full width
                },
                {
                    name: 'description',
                    type: 'textarea',
                    label: 'Description',
                    rows: 5,
                    col: 2 // Full width
                },
                {
                    name: 'category_id',
                    type: 'select',
                    label: 'Category',
                    options: this.categoryOptions,
                    col: 1 // Half width
                },
                {
                    name: 'subcategory_id',
                    type: 'select',
                    label: 'Subcategory',
                    options: this.subcategoryOptions,
                    col: 1 // Half width
                }
            ];

            return fields;
        },

        categoryOptions() {
            return [
                { value: '', label: 'Select category' },
                ...(this.documents.categories || []).map(cat => ({
                    value: cat.id,
                    label: cat.name
                }))
            ];
        },

        subcategoryOptions() {
            if (!this.document || !this.document.category_id) {
                return [{ value: '', label: 'Select a category first', disabled: true }];
            }

            const subCategories = (this.documents.subcategories || []).filter(
                sub => sub.category_id == this.document.category_id
            );

            if (subCategories.length === 0) {
                return [{ value: '', label: 'No subcategories available', disabled: true }];
            }

            return [
                { value: '', label: 'Select subcategory' },
                ...subCategories.map(sub => ({
                    value: sub.id,
                    label: sub.name
                }))
            ];
        }
    },

    methods: {
        handleOpened() {
            
            if (this.documentData) {
                this.document = {
                    id: this.documentData.id,
                    name: this.documentData.name,
                    description: this.documentData.description,
                    category_id: this.documentData.category_id ? parseInt(this.documentData.category_id) : '',
                    subcategory_id: this.documentData.subcategory_id ? parseInt(this.documentData.subcategory_id) : ''
                };
            }
        },

        handleInput(updatedDocument) {
            const categoryChanged = this.document && this.document.category_id !== updatedDocument.category_id;
            this.document = updatedDocument;
            
            if (categoryChanged) {
                this.$set(this.document, 'subcategory_id', '');
            }
        },

        hideModal() {
            this.$modal.hide('document-edit');
        },

        handleClosed() {
            // Reset form
            this.document = null;
            this.formErrors = {};
            this.saving = false;
        },

        save() {
            if (!this.document) return;
            this.saving = true;
            this.formErrors = {};

            axios.patch('/documents/' + this.document.id, {
                document: this.document
            }).then(response => {
                this.$emit('document-updated', response.data.documents.document);

                this.$notify({
                    type: 'success',
                    title: 'Success',
                    text: response.data.message
                });

                this.hideModal();
            }).catch(error => {
                this.formErrors = error.response?.data?.errors || {};
                
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: error.response?.data?.message || 'Failed to save document'
                });
            }).finally(() => {
                this.saving = false;
            });
        }
    }
};
</script>
